import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getAvailableCurrencies } from '../data/users';

// Default currency info mapping
const currencyInfo = {
  USD: { symbol: 'USD', name: 'US Dollar' },
  EUR: { symbol: 'EUR', name: 'Euro' },
  GBP: { symbol: 'GBP', name: 'British Pound' },
  JPY: { symbol: 'JPY', name: 'Japanese Yen' },
  CNY: { symbol: 'CNY', name: 'Chinese Yuan' },
  KRW: { symbol: 'KRW', name: 'South Korean Won' },
  INR: { symbol: 'INR', name: 'Indian Rupee' },
  IQD: { symbol: 'IQD', name: 'Iraqi Dinar' },
  TND: { symbol: 'TND', name: 'Tunisian Dinar' },
  KWD: { symbol: 'KWD', name: 'Kuwaiti Dinar' },
  LBP: { symbol: 'LBP', name: 'Lebanese Pound' },
  AUD: { symbol: 'AUD', name: 'Australian Dollar' },
  CAD: { symbol: 'CAD', name: 'Canadian Dollar' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc' },
  AED: { symbol: 'AED', name: 'UAE Dirham' },
  SGD: { symbol: 'SGD', name: 'Singapore Dollar' },
  MXN: { symbol: 'MXN', name: 'Mexican Peso' },
  BTC: { symbol: 'BTC', name: 'Bitcoin' },
  ETH: { symbol: 'ETH', name: 'Ethereum' }
};

const CurrencySelector = ({ value, onChange, className = '', compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const ref = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        setIsLoading(true);
        const currencies = await getAvailableCurrencies();
        setAvailableCurrencies(currencies);
        
        // If no currency is selected and we have currencies, select the first one
        if (!value && currencies.length > 0) {
          onChange(currencies[0]);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get currency info, use default info or create basic info if not found
  const getCurrencyInfo = (code) => {
    return currencyInfo[code] || { symbol: code, name: code };
  };

  const selectedCurrency = value || (availableCurrencies.length > 0 ? availableCurrencies[0] : 'USD');
  const selectedInfo = getCurrencyInfo(selectedCurrency);

  // Calculate popup position
  const updatePosition = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Determine if the dropdown should appear above or below the button
      const showBelow = spaceBelow >= 300 || spaceBelow > spaceAbove;
      
      // Align to the button but ensure it stays within the viewport
      let leftPos = rect.left;
      if (rect.left + rect.width > windowWidth - 10) {
        // If dropdown would extend beyond right edge, adjust left position
        leftPos = Math.max(10, windowWidth - rect.width - 10);
      }
      
      setPosition({
        top: showBelow ? rect.bottom + 4 : rect.top - Math.min(300, spaceAbove - 10),
        left: leftPos,
        width: rect.width
      });
    }
  };

  useEffect(() => {
    if (isOpen && ref.current) {
      updatePosition();
      const handleResize = () => updatePosition();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen]);

  if (isLoading) {
    return compact ? (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400">
        <div className="w-8 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
      </div>
    ) : (
      <div className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 ${className}`}>
        <div className="w-16 text-left font-medium">...</div>
        <span>Loading currencies...</span>
      </div>
    );
  }

  return (
    <div className={`relative ${compact ? 'flex-shrink-0' : className}`}>
      <button
        ref={ref}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setTimeout(updatePosition, 0);
          }
        }}
        className={compact 
          ? "flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-semibold rounded-lg bg-teal-500 hover:bg-teal-600 active:bg-teal-700 text-white transition-all shadow-sm active:scale-95"
          : "flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full"
        }
      >
        {compact ? (
          <>
            <span className="text-sm">{selectedInfo.symbol}</span>
            <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        ) : (
          <>
            <div className="w-16 text-left font-medium text-primary-600 dark:text-primary-400">{selectedInfo.symbol}</div>
            <span className="flex-1 text-left text-gray-600 dark:text-gray-400">
              {selectedInfo.name}
            </span>
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {isOpen && (
        <>
          {ReactDOM.createPortal(
            <>
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-[8000]"
                onClick={() => setIsOpen(false)}
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
              />
              
              <div 
                className="fixed z-[8001] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-y-auto"
                style={{ 
                  position: 'fixed',
                  top: `${position.top}px`,
                  left: compact ? 'auto' : `${position.left}px`,
                  right: compact ? '16px' : 'auto',
                  width: compact ? '200px' : `${position.width}px`,
                  maxWidth: '95vw',
                  maxHeight: '300px'
                }}
              >
                {availableCurrencies.map((code) => {
                  const info = getCurrencyInfo(code);
                  const isSelected = code === selectedCurrency;
                  return (
                    <button
                      key={code}
                      onClick={() => {
                        onChange(code);
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors active:scale-[0.98] ${
                        isSelected
                          ? 'bg-teal-50 dark:bg-teal-900/20'
                          : ''
                      }`}
                    >
                      {compact ? (
                        <>
                          <div className={`flex-1 text-left font-semibold ${
                            isSelected
                              ? 'text-teal-600 dark:text-teal-400'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}>
                            {info.symbol}
                          </div>
                          {isSelected && (
                            <CheckIcon className="w-4 h-4 text-teal-500 flex-shrink-0" />
                          )}
                        </>
                      ) : (
                        <>
                          <div className={`w-16 text-left font-medium ${
                            isSelected
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}>
                            {info.symbol}
                          </div>
                          <div className="flex-1 text-left text-gray-600 dark:text-gray-400">
                            {info.name}
                          </div>
                          {isSelected && (
                            <CheckIcon className="w-4 h-4 text-primary-500" />
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </>,
            document.body
          )}
        </>
      )}
    </div>
  );
};

export default CurrencySelector;